import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  TextInput,
  Select,
  Pagination,
  Group,
  ActionIcon,
  Text,
  Alert,
} from "@mantine/core";
import { IconEdit, IconTrash, IconPlus, IconSearch } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

const GenericCRUD = ({
  title,
  api,
  columns,
  formFields,
  searchFields = [],
  canCreate = true,
  canEdit = true,
  canDelete = true,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({});

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      const filters = searchTerm ? { search: searchTerm } : {};
      const result = await api.getAll({ ...filters, ...pagination });

      setData(result.data || []);
      setPagination((prev) => ({
        ...prev,
        total: result.pagination?.total || 0,
      }));
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load data",
        color: "red",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [pagination.page, searchTerm]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedItem) {
        await api.update(selectedItem.id, formData);
        notifications.show({
          title: "Success",
          message: `${title} updated successfully`,
          color: "green",
        });
      } else {
        await api.create(formData);
        notifications.show({
          title: "Success",
          message: `${title} created successfully`,
          color: "green",
        });
      }

      setModalOpen(false);
      setFormData({});
      setSelectedItem(null);
      loadData();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Operation failed",
        color: "red",
      });
    }
    setLoading(false);
  };

  // Handle delete
  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this ${title.toLowerCase()}?`)) return;

    try {
      await api.delete(item.id);
      notifications.show({
        title: "Success",
        message: `${title} deleted successfully`,
        color: "green",
      });
      loadData();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to delete",
        color: "red",
      });
    }
  };

  // Open modal for create/edit
  const openModal = (item = null) => {
    setSelectedItem(item);
    setFormData(item || {});
    setModalOpen(true);
  };

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md">
        <Text size="xl" weight={500}>
          {title} Management
        </Text>
        {canCreate && (
          <Button
            leftSection={<IconPlus size="1rem" />}
            onClick={() => openModal()}
          >
            Add {title}
          </Button>
        )}
      </Group>

      {/* Search */}
      {searchFields.length > 0 && (
        <TextInput
          placeholder={`Search ${title.toLowerCase()}...`}
          leftSection={<IconSearch size="1rem" />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          mb="md"
        />
      )}

      {/* Table */}
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            {columns.map((col) => (
              <Table.Th key={col.key}>{col.label}</Table.Th>
            ))}
            {(canEdit || canDelete) && <Table.Th>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((item) => (
            <Table.Tr key={item.id}>
              {columns.map((col) => (
                <Table.Td key={col.key}>
                  {col.render ? col.render(item[col.key], item) : item[col.key]}
                </Table.Td>
              ))}
              {(canEdit || canDelete) && (
                <Table.Td>
                  <Group gap="xs">
                    {canEdit && (
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={() => openModal(item)}
                      >
                        <IconEdit size="1rem" />
                      </ActionIcon>
                    )}
                    {canDelete && (
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => handleDelete(item)}
                      >
                        <IconTrash size="1rem" />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* Pagination */}
      <Group justify="center" mt="md">
        <Pagination
          total={Math.ceil(pagination.total / pagination.limit)}
          value={pagination.page}
          onChange={(page) => setPagination((prev) => ({ ...prev, page }))}
        />
      </Group>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${selectedItem ? "Edit" : "Create"} ${title}`}
      >
        <form onSubmit={handleSubmit}>
          {formFields.map((field) => (
            <div key={field.key} style={{ marginBottom: "1rem" }}>
              {field.type === "select" ? (
                <Select
                  label={field.label}
                  required={field.required}
                  data={field.options}
                  value={formData[field.key] || ""}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, [field.key]: value }))
                  }
                />
              ) : (
                <TextInput
                  label={field.label}
                  required={field.required}
                  value={formData[field.key] || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                />
              )}
            </div>
          ))}

          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              {selectedItem ? "Update" : "Create"}
            </Button>
          </Group>
        </form>
      </Modal>
    </Container>
  );
};

export default GenericCRUD;
